"use client";

import { useState } from "react";
import {
  CopyButton,
  ErrorNotice,
  Notice,
  SelectField,
  TextField,
} from "@/components/tool-kit/primitives";
import { WIDGET_H2, WIDGET_H3 } from "@/components/tool-kit/styles";
import {
  ConversionStats,
  ConversionTables,
  GapTable,
  NAME_KEYS,
  rationalText,
  TransferResult,
} from "@/components/tools/cs-tools/storage-views";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { normalizeNumberInput } from "@/lib/parse-number";
import {
  isZero,
  multiply,
  parseDecimal,
  type Rational,
  ratio,
  toBits,
  transferSeconds,
  UNITS,
  type UnitId,
  unitOf,
} from "@/lib/tool-kit/cs-tools/storage";

/** English strings. A translated content file overrides these through `ui`. */
export const DATA_STORAGE_CONVERTER_STRINGS = {
  valueLabel: "Amount",
  valueHint:
    "A size such as 500, 1.5 or 256. Convert between bits, bytes, decimal units (kB, MB, GB) and binary units (KiB, MiB, GiB).",
  valueDefault: "1",
  unitLabel: "Unit",
  errEmpty: "Enter an amount to convert.",
  errInvalid:
    "That is not a valid size. Use digits with an optional decimal point; a size cannot be negative.",
  errTooLong: "That number is too long. Shorten it to 40 characters or fewer.",
  resultsHeading: "Result",
  statBytes: "Bytes",
  statBits: "Bits",
  statCounterpart: "The same {from} in {system} units",
  systemSi: "decimal",
  systemIec: "binary",
  tablesHeading: "Every unit",
  groupBase: "Bits and bytes",
  groupSiBytes: "Decimal (SI) bytes — powers of 1000",
  groupIecBytes: "Binary (IEC) bytes — powers of 1024",
  groupBits: "Bits — decimal and binary",
  colUnit: "Unit",
  colName: "Name",
  colValue: "Value",
  colSpeed: "Speed",
  copyBytes: "Copy bytes",
  copied: "Copied!",
  gapHeading: "Why 1000 and 1024 are not the same",
  gapBody:
    "Each decimal prefix is slightly smaller than its binary twin, and the gap compounds at every step. This is why a drive sold as 1 TB shows up as about 931 GiB in software that counts in powers of 1024.",
  gapCaption: "Decimal against binary prefixes",
  colPair: "Decimal / binary",
  colSiBytes: "Decimal size in bytes",
  colIecBytes: "Binary size in bytes",
  colShortfall: "Decimal is smaller by",
  transferHeading: "Transfer time",
  transferBody:
    "How long the amount above takes to move at a given speed. Network speeds are quoted in bits per second; file sizes are in bytes.",
  rateLabel: "Speed",
  rateHint: "For example 100 for a 100 Mbit/s connection.",
  rateDefault: "1",
  rateUnitLabel: "Speed unit",
  efficiencyLabel: "Link efficiency (%)",
  efficiencyHint:
    "Real transfers rarely reach the advertised speed — protocol overhead and congestion take a share. 100 means the full line rate.",
  efficiencyDefault: "100",
  errRate: "Enter a speed greater than zero.",
  errEfficiency: "Enter a percentage above 0 and up to 100.",
  statTime: "Transfer time",
  statSeconds: "Seconds",
  speedCaption: "The effective speed in other units",
  durYears: "{n} y",
  durDays: "{n} d",
  durHours: "{n} h",
  durMinutes: "{n} min",
  durSeconds: "{n} s",
  durMs: "{n} ms",
  durUnderMs: "under 1 ms",
  nameBit: "bit",
  nameB: "byte",
  nameKB: "kilobyte",
  nameMB: "megabyte",
  nameGB: "gigabyte",
  nameTB: "terabyte",
  namePB: "petabyte",
  nameEB: "exabyte",
  nameZB: "zettabyte",
  nameYB: "yottabyte",
  nameKiB: "kibibyte",
  nameMiB: "mebibyte",
  nameGiB: "gibibyte",
  nameTiB: "tebibyte",
  namePiB: "pebibyte",
  nameEiB: "exbibyte",
  nameZiB: "zebibyte",
  nameYiB: "yobibyte",
  nameKbit: "kilobit",
  nameMbit: "megabit",
  nameGbit: "gigabit",
  nameTbit: "terabit",
  namePbit: "petabit",
  nameKibit: "kibibit",
  nameMibit: "mebibit",
  nameGibit: "gibibit",
  nameTibit: "tebibit",
  namePibit: "pebibit",
} as const;

const RATE_UNITS: UnitId[] = [
  "bit",
  "kbit",
  "Mbit",
  "Gbit",
  "Tbit",
  "B",
  "kB",
  "MB",
  "GB",
  "KiB",
  "MiB",
  "GiB",
];

function readDecimal(text: string) {
  return parseDecimal(normalizeNumberInput(text) ?? text);
}

function readPositive(text: string): Rational | null {
  const parsed = readDecimal(text);
  return parsed.ok && !isZero(parsed.value) ? parsed.value : null;
}

const SIZE_ERRORS = {
  empty: "errEmpty",
  invalid: "errInvalid",
  tooLong: "errTooLong",
} as const;

export default function DataStorageConverterWidget() {
  const strings = useToolUi(DATA_STORAGE_CONVERTER_STRINGS);
  const fmt = useToolNumber();
  const locale = useToolLocale();
  const [valueText, setValueText] = useState<string>(strings.valueDefault);
  const [unitId, setUnitId] = useState<UnitId>("TB");
  const [rateText, setRateText] = useState<string>(strings.rateDefault);
  const [rateUnitId, setRateUnitId] = useState<UnitId>("Gbit");
  const [efficiencyText, setEfficiencyText] = useState<string>(
    strings.efficiencyDefault
  );

  const parsed = readDecimal(valueText);
  const unit = unitOf(unitId);
  const bits = parsed.ok ? toBits(parsed.value, unit) : null;

  const rate = readPositive(rateText);
  const efficiency = readPositive(efficiencyText);
  const efficiencyValid =
    efficiency !== null && efficiency.n <= efficiency.d * BigInt(100);
  const rateBits = rate ? toBits(rate, unitOf(rateUnitId)) : null;
  const seconds =
    bits && rateBits && efficiency && efficiencyValid
      ? transferSeconds(bits, rateBits, efficiency)
      : null;
  const effectiveRate =
    rateBits && efficiency && efficiencyValid
      ? multiply(rateBits, multiply(efficiency, ratio(BigInt(1), BigInt(100))))
      : null;

  const unitOptions = UNITS.map((u) => ({
    value: u.id,
    label: `${strings[NAME_KEYS[u.id]]} (${u.id})`,
  }));
  const rateOptions = RATE_UNITS.map((id) => ({
    value: id,
    label: `${id}/s`,
  }));

  return (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-2">
        <TextField
          hint={strings.valueHint}
          id="storage-value"
          label={strings.valueLabel}
          mono
          onChange={setValueText}
          value={valueText}
        />
        <SelectField
          id="storage-unit"
          label={strings.unitLabel}
          onChange={setUnitId}
          options={unitOptions}
          value={unitId}
        />
      </div>

      <div aria-live="polite" className="space-y-5">
        {parsed.ok ? null : (
          <ErrorNotice>{strings[SIZE_ERRORS[parsed.error]]}</ErrorNotice>
        )}
        {bits ? (
          <>
            <h2 className={WIDGET_H2}>{strings.resultsHeading}</h2>
            <ConversionStats
              bits={bits}
              locale={locale}
              strings={strings}
              unit={unit}
            />
            <CopyButton
              copiedLabel={strings.copied}
              label={strings.copyBytes}
              text={rationalText(
                { n: bits.n, d: bits.d * BigInt(8) },
                "en",
                30
              ).replaceAll(",", "")}
            />
            <ConversionTables bits={bits} locale={locale} strings={strings} />
            <GapTable fmt={fmt} locale={locale} strings={strings} />
          </>
        ) : null}
      </div>

      <section className="space-y-4">
        <h3 className={WIDGET_H3}>{strings.transferHeading}</h3>
        <p className="text-muted-foreground text-sm">{strings.transferBody}</p>
        <div className="grid gap-4 md:grid-cols-3">
          <TextField
            hint={strings.rateHint}
            id="storage-rate"
            label={strings.rateLabel}
            mono
            onChange={setRateText}
            value={rateText}
          />
          <SelectField
            id="storage-rate-unit"
            label={strings.rateUnitLabel}
            onChange={setRateUnitId}
            options={rateOptions}
            value={rateUnitId}
          />
          <TextField
            hint={strings.efficiencyHint}
            id="storage-efficiency"
            label={strings.efficiencyLabel}
            mono
            onChange={setEfficiencyText}
            value={efficiencyText}
          />
        </div>
        <div aria-live="polite" className="space-y-4">
          {rate ? null : <ErrorNotice>{strings.errRate}</ErrorNotice>}
          {efficiencyValid ? null : (
            <ErrorNotice>{strings.errEfficiency}</ErrorNotice>
          )}
          {seconds && effectiveRate ? (
            <TransferResult
              fmt={fmt}
              locale={locale}
              rateBits={effectiveRate}
              seconds={seconds}
              strings={strings}
            />
          ) : null}
          {bits ? null : <Notice>{strings.errEmpty}</Notice>}
        </div>
      </section>
    </div>
  );
}
